import { PrimeReactProvider } from "primereact/api";
import { ThemeProvider } from "./themes/Theme";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { Main } from "./pages/main/Main";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import {
  Route,
  HashRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import { PrivacyModeProvider } from "./context-providers/Privacy";
import { ConfirmDialog } from "primereact/confirmdialog";
import { ToastProvider } from "./context-providers/Toast";
import * as Sentry from "@sentry/react";
import { ApiError } from "./client/core/ApiError";
import { augmentRequestError, isCurrentUserDeveloper } from "./sentry-globals";
import { updateSentryRouteTags } from "./sentry-route-tags";
import { UpdateContextProvider } from "./context-providers/UpdateContext";
import { ReportDialogsProvider } from "./pages/specialty-assistant/dashboard/ReportActionMenu";

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (err, _variables, _context, mutation) => {
      Sentry.withScope((scope) => {
        scope.setContext("mutation", {
          mutationId: mutation.mutationId,
          mutationKey: mutation.options.mutationKey,
          variables: mutation.state.variables,
        });
        const mutationKey = mutation.options.mutationKey;
        if (mutationKey) {
          // Mutation keys are [staticName, ...ids]; fingerprint on the static
          // name only, so one bug groups into one Sentry issue instead of one
          // per report. The ids stay visible via the mutation context above.
          scope.setFingerprint([String(mutationKey[0])]);
          scope.setTag("mutation", String(mutationKey[0]));
          // A string second element is always the report id by convention.
          // It beats the route's report_id: dashboard rows mutate reports
          // without opening them.
          if (typeof mutationKey[1] === "string") {
            scope.setTag("report_id", mutationKey[1]);
          }
        }
        augmentRequestError(scope, err);
        Sentry.captureException(err);
      });
    },
  }),
  queryCache: new QueryCache({
    onError: (err, query) => {
      // Developers switch logins often, so stale tabs trigger 400s when they
      // request a report from the previous client under the new client's credentials.
      if (
        err instanceof ApiError &&
        err.status === 400 &&
        err.request.url === "/asgard/list_report/{report_id}" &&
        isCurrentUserDeveloper()
      ) {
        console.warn("Dropping developer list_report error");
        return;
      }
      Sentry.withScope((scope) => {
        scope.setContext("query", { queryHash: query.queryHash });
        scope.setFingerprint([query.queryHash.replaceAll(/[0-9]/g, "0")]);
        scope.setTag("query", String(query.queryKey[0]));
        augmentRequestError(scope, err);
        Sentry.captureException(err);
      });
    },
  }),
});

const SentryRouteTags = () => {
  // Set during render, not in an effect, so errors thrown while rendering the
  // new route already carry its tags.
  updateSentryRouteTags(useLocation().pathname);
  return null;
};

function App() {
  return (
    <Router>
      <SentryRouteTags />
      <PrimeReactProvider value={{ zIndex: { toast: 2000 } }}>
        <ThemeProvider>
          <PrivacyModeProvider>
            <QueryClientProvider client={queryClient}>
              <UpdateContextProvider>
                <ToastProvider>
                  <ReportDialogsProvider>
                    <Routes>
                      <Route path="*" element={<Main />} />
                    </Routes>
                  </ReportDialogsProvider>
                </ToastProvider>
              </UpdateContextProvider>
            </QueryClientProvider>
          </PrivacyModeProvider>
        </ThemeProvider>
        <ConfirmDialog />
      </PrimeReactProvider>
    </Router>
  );
}

export default App;
